import { useState, useRef } from 'react'
import clsx from 'clsx'

/**
 * AttachmentCell
 *
 * Handles screenshot/evidence uploads for a test row.
 * - Drag-and-drop or click to upload
 * - Only fully active (shows upload zone) when isFail = true
 * - Shows thumbnail previews of uploaded images
 * - Delete button on each attachment
 *
 * Props:
 *   cls         — tailwind width classes
 *   attachments — array of attachment objects
 *   isFail      — bool — controls whether upload is visible
 *   rowId       — number (for aria/key)
 *   onUpload    — (File, caption) => void
 *   onDelete    — (attachmentId) => void
 */
export default function AttachmentCell({ cls, attachments, isFail, rowId, onUpload, onDelete }) {
  const [dragging, setDragging]   = useState(false)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview]     = useState(null)  // { url, name }
  const inputRef = useRef(null)

  const handleFile = async (file) => {
    if (!file) return
    setUploading(true)
    await onUpload(file, '')
    setUploading(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handlePaste = (e) => {
    const item = [...e.clipboardData.items].find(i => i.type.startsWith('image/'))
    if (item) {
      const file = item.getAsFile()
      if (file) handleFile(file)
    }
  }

  return (
    <div
      className={clsx(cls, 'px-2 py-2 flex flex-col gap-1.5 justify-center relative')}
      onPaste={handlePaste}
    >
      {/* Existing attachments */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {attachments.map(att => (
            <div key={att.id} className="relative group/att">
              {att.mime_type?.startsWith('image/') ? (
                <button
                  type="button"
                  onClick={() => setPreview({ url: att.url, name: att.original_filename })}
                  className="w-10 h-10 rounded-lg overflow-hidden border border-white/15 hover:border-purple-500/40 transition-all block"
                >
                  <img
                    src={att.url}
                    alt={att.original_filename}
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <a
                  href={att.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-lg border border-white/15 hover:border-purple-500/40 bg-white/4 text-white/40 text-lg transition-all"
                >
                  📄
                </a>
              )}
              {/* Delete button */}
              <button
                type="button"
                onClick={() => onDelete(att.id)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center opacity-0 group-hover/att:opacity-100 transition-opacity z-10 hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload zone — only shown for fail rows OR on hover */}
      {isFail && (
        <div
          onDragEnter={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={clsx(
            'flex items-center gap-1.5 px-2 py-1.5 rounded-lg border cursor-pointer transition-all text-[10px] font-medium',
            dragging
              ? 'border-red-400/60 bg-red-500/15 text-red-300'
              : 'border-dashed border-white/15 hover:border-red-400/40 hover:bg-red-500/8 text-white/35 hover:text-white/60',
            uploading && 'opacity-50 cursor-wait'
          )}
        >
          {uploading ? (
            <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
          ) : (
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
            </svg>
          )}
          <span>{uploading ? 'Uploading…' : attachments.length === 0 ? 'Add evidence' : '+ more'}</span>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        className="sr-only"
        onChange={e => handleFile(e.target.files?.[0])}
      />

      {/* Lightbox preview */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setPreview(null)}
        >
          <div className="relative max-w-3xl max-h-[85vh] bg-cosmic-900 rounded-2xl overflow-hidden border border-white/12 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="text-xs font-mono text-white/60 truncate max-w-xs">{preview.name}</span>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="text-white/30 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto" onClick={e => e.stopPropagation()}>
              <img src={preview.url} alt={preview.name} className="max-w-full h-auto rounded-lg" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
